import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FloorMapStudio } from 'components/FloorMapStudio';
import {
  FloorMapHistoryApi,
  FloorMapLayersApi,
} from 'components/FloorMapStudio/FloorMapStudio.types';
import useApiContext from '../../hooks/useApiContext';
import useRepo from '../../hooks/useRepo';
import { iFloorMapData } from 'services/FloorMap/FloorMap.type';
import { iExhibitorListRest } from 'services/Exhibitor/Exhibitor.type';

function FloorMap(): JSX.Element {
  const { eventId } = useParams();
  const { floorMapRepository, exhibitorRepository } = useApiContext();
  const [saving, setSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  const { response: floorMapData, loading: loadingMap } =
    useRepo<iFloorMapData | null>(
      async () =>
        eventId !== undefined
          ? await floorMapRepository.getFloorMap(eventId)
          : null,
      [eventId]
    );

  useEffect(() => {
    if (floorMapData) setIsOnline(floorMapData.is_online !== false);
  }, [floorMapData]);

  const handleToggleOnline = useCallback(
    async (value: boolean) => {
      if (!eventId) return;
      const previous = isOnline;
      setIsOnline(value);
      try {
        await floorMapRepository.setOnline(eventId, value);
      } catch (err: any) {
        console.error('Online toggle failed:', err);
        setIsOnline(previous);
      }
    },
    [eventId, floorMapRepository, isOnline]
  );

  const history = useMemo<FloorMapHistoryApi | undefined>(
    () =>
      eventId === undefined
        ? undefined
        : {
            listVersions: async (forkerId) =>
              await floorMapRepository.getVersions(eventId, forkerId),
            getVersion: async (versionId) =>
              await floorMapRepository.getVersion(eventId, versionId),
            restoreVersion: async (versionId) =>
              await floorMapRepository.restoreVersion(eventId, versionId),
          },
    [eventId, floorMapRepository]
  );

  const layers = useMemo<FloorMapLayersApi | undefined>(
    () =>
      eventId === undefined
        ? undefined
        : {
            list: async () => await floorMapRepository.getLayers(eventId),
            create: async (data) =>
              await floorMapRepository.createLayer(eventId, data),
            update: async (id, data) =>
              await floorMapRepository.updateLayer(eventId, id, data),
            remove: async (id) =>
              await floorMapRepository.deleteLayer(eventId, id),
          },
    [eventId, floorMapRepository]
  );

  const { response: exhibitors, loading: loadingExhibitors } = useRepo<
    iExhibitorListRest[]
  >(
    async () =>
      eventId !== undefined
        ? await exhibitorRepository.getExhibitors(eventId)
        : [],
    [eventId]
  );

  // Only the visible exhibitors of this event can be placed on the map
  const visibleExhibitors = useMemo(
    () =>
      (exhibitors ?? []).filter((e) => Number(e.exhibitor_visibility) === 1),
    [exhibitors]
  );

  const handleSave = useCallback(
    async (data: Partial<iFloorMapData>) => {
      if (!eventId) return;
      setSaving(true);
      try {
        const res = await floorMapRepository.saveFloorMap(eventId, data);
        // A first save creates the row with the default flag: apply a toggle chosen before it
        if (!floorMapData && !isOnline && res.floor_map?.is_online !== false) {
          await floorMapRepository.setOnline(eventId, false);
        }
      } catch (err: any) {
        console.error('Save failed:', err);
      } finally {
        setSaving(false);
      }
    },
    [eventId, floorMapRepository, floorMapData, isOnline]
  );

  if (loadingMap || loadingExhibitors) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <FloorMapStudio
      initialData={floorMapData ?? null}
      exhibitors={visibleExhibitors}
      onSave={handleSave}
      saving={saving}
      history={history}
      layers={layers}
      isOnline={isOnline}
      onToggleOnline={handleToggleOnline}
    />
  );
}

export default FloorMap;
